import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import * as Mui from "@mui/material";
import { useTheme } from "@mui/material/styles";
import * as yup from "yup";
import { FormField, FormSelectField } from "component/shared/form-fields";
import { User } from "model/user";
import { BillingCardResource } from "api/resource/billing";
import { loadRecurly } from "shared/recurly";
import type { Recurly, RecurlyElement, RecurlyElementStyle, RecurlyElements } from "shared/recurly-js";
import { validate } from "shared/yup";
import { stateOptionsFor } from "shared/us-states";
import { BillingCardUpdateError, updateBillingCard } from "redux/slice/billing";
import { useAppThunkDispatch } from "redux/store";
import {
	AddressField,
	CARD_ERROR_MESSAGES,
	CARD_FIELD_MESSAGES,
	CardField,
	cardErrorMessage,
	requiredMessage,
	tokenFieldErrors,
} from "./card-errors";
import { formatCardExpiry } from "./billing-format";
import { useBillingStyles } from "./styles";

type FieldErrors = Partial<Record<CardField | AddressField, string>>;
type Address = Record<AddressField, string>;

interface ElementState {
	valid: boolean;
	empty: boolean;
}

// Recurly.js v4 has no combined expiry element, so the one MM/YY field is a month and a year
// element sharing a border. The two read as one field to the user and to the error copy.
type ElementField = "number" | "month" | "year" | "cvv";

const ELEMENT_FIELDS: ElementField[] = ["number", "month", "year", "cvv"];

const INITIAL_CARD_STATE: Record<ElementField, ElementState> = {
	number: { valid: false, empty: true },
	month: { valid: false, empty: true },
	year: { valid: false, empty: true },
	cvv: { valid: false, empty: true },
};

const fieldOf = (element: ElementField): CardField => (element === "month" || element === "year" ? "expiry" : element);

const addressSchema = {
	address1: yup.string().trim().required(requiredMessage("address1")),
	city: yup.string().trim().required(requiredMessage("city")),
	state: yup.string().trim().required(requiredMessage("state")),
	zip: yup.string().trim().required(requiredMessage("zip")),
};

const addressFromCard = (card?: BillingCardResource | null): Address => ({
	address1: card?.address1 ?? "",
	city: card?.city ?? "",
	state: card?.state ?? "",
	zip: card?.zip ?? "",
});

/**
 * The hosted fields can't be pre-filled or show a mask, so the card on file is named in each
 * field's placeholder instead. The number and expiry come from the card; a CVV is never known.
 */
const placeholdersFor = (card?: BillingCardResource | null): Record<ElementField, string> => {
	const onFile = card && card.paymentType === "card" && card.lastFour;
	return {
		number: onFile
			? `${card.cardType ? `${card.cardType} ` : ""}•••• •••• •••• ${card.lastFour}`
			: "0000 0000 0000 0000",
		month: (onFile && formatCardExpiry(card)?.split("/")[0]) || "MM",
		year: (onFile && formatCardExpiry(card)?.split("/")[1]) || "YY",
		cvv: onFile ? "•••" : "CVV",
	};
};

interface Props {
	publicKey: string;
	user: User;
	card?: BillingCardResource | null;
	onSaved: () => void;
	onError: (message: string) => void;
	onLoadError: () => void;
	/** Reports the form's state for the save bar, and null once the form unmounts. */
	onStateChange: (state: CardFormState | null) => void;
}

export interface CardFormState {
	dirty: boolean;
	saving: boolean;
}

export interface CardFormHandle {
	save: () => void;
	cancel: () => void;
}

/** The always-open Payment Method fields. */
export const CardForm = forwardRef<CardFormHandle, Props>(({ publicKey, user, card, onSaved, onError, onLoadError, onStateChange }, ref) => {
	const { classes, cx } = useBillingStyles();
	const theme = useTheme();
	const dispatch = useAppThunkDispatch();
	const numberRef = useRef<HTMLDivElement>(null);
	const monthRef = useRef<HTMLDivElement>(null);
	const yearRef = useRef<HTMLDivElement>(null);
	const cvvRef = useRef<HTMLDivElement>(null);
	const recurlyRef = useRef<Recurly>();
	const elementsRef = useRef<RecurlyElements>();
	const mountedRef = useRef(false);
	const [ready, setReady] = useState(false);
	const [saving, setSaving] = useState(false);
	const [submitted, setSubmitted] = useState(false);
	// Bumped by Cancel to remount the hosted fields, which can't be cleared from outside.
	const [resetKey, setResetKey] = useState(0);
	const [cardState, setCardState] = useState(INITIAL_CARD_STATE);
	const [touched, setTouched] = useState<Partial<Record<ElementField | AddressField, boolean>>>({});
	const [tokenErrors, setTokenErrors] = useState<FieldErrors>({});
	const initialAddress = useMemo(() => addressFromCard(card), [card]);
	const [address, setAddress] = useState<Address>(initialAddress);
	const placeholders = placeholdersFor(card);
	const { number: numberPlaceholder, month: monthPlaceholder, year: yearPlaceholder, cvv: cvvPlaceholder } = placeholders;

	// The theme object is rebuilt on every render of Base, so the hosted fields depend on the
	// values taken from it: a new theme with the same values must not remount them, which wipes
	// whatever has been typed.
	const { fontFamily } = theme.typography;
	const { textPrimary, textSecondary } = theme.palette.custom;
	const elementStyle = useMemo<RecurlyElementStyle>(() => ({
		fontFamily,
		fontSize: "16px",
		lineHeight: "24px",
		fontColor: textPrimary,
		padding: "0 16px",
	}), [fontFamily, textPrimary]);

	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
		};
	}, []);

	useEffect(() => {
		let active = true;
		const attached: RecurlyElement[] = [];
		// A re-run starts from scratch: stale ready/validity from the elements being replaced would
		// let Save run against fields that are no longer mounted.
		setReady(false);
		setCardState(INITIAL_CARD_STATE);
		const placeholderFor: Record<ElementField, string> = {
			number: numberPlaceholder,
			month: monthPlaceholder,
			year: yearPlaceholder,
			cvv: cvvPlaceholder,
		};
		loadRecurly(publicKey)
			.then((recurly) => {
				if (!active) {
					return;
				}
				const elements = recurly.Elements();
				const fields: [ElementField, HTMLDivElement | null, (style: RecurlyElementStyle) => RecurlyElement][] = [
					["number", numberRef.current, (style) => elements.CardNumberElement({ style })],
					["month", monthRef.current, (style) => elements.CardMonthElement({ style })],
					["year", yearRef.current, (style) => elements.CardYearElement({ style })],
					["cvv", cvvRef.current, (style) => elements.CardCvvElement({ style })],
				];
				fields.forEach(([field, container, create]) => {
					if (!container) {
						return;
					}
					const element = create({
						...elementStyle,
						placeholder: { content: placeholderFor[field], color: textSecondary },
					});
					element.on("change", (state) => {
						setCardState((prev) => ({ ...prev, [field]: { valid: state.valid, empty: state.empty } }));
						setTokenErrors((prev) => (prev[fieldOf(field)] ? { ...prev, [fieldOf(field)]: undefined } : prev));
					});
					element.on("blur", () => setTouched((prev) => ({ ...prev, [field]: true })));
					element.attach(container);
					attached.push(element);
				});
				recurlyRef.current = recurly;
				elementsRef.current = elements;
				setReady(true);
			})
			.catch((error) => {
				// Anything thrown while building the elements lands here too, so it is logged rather than
				// left to look like the payment system being down.
				// eslint-disable-next-line no-console
				console.error("[billing] Recurly card fields failed to load", error);
				if (active) {
					onLoadError();
				}
			});
		return () => {
			active = false;
			attached.forEach((element) => element.remove());
			recurlyRef.current = undefined;
			elementsRef.current = undefined;
		};
	}, [publicKey, elementStyle, textSecondary, numberPlaceholder, monthPlaceholder, yearPlaceholder, cvvPlaceholder, resetKey, onLoadError]);

	const addressErrors = validate(addressSchema, address);

	const cardError = (field: CardField) => {
		if (tokenErrors[field]) {
			return tokenErrors[field];
		}
		const elements: ElementField[] = field === "expiry" ? ["month", "year"] : [field];
		const valid = elements.every((element) => cardState[element].valid);
		const shown = submitted || elements.every((element) => touched[element]);
		return shown && !valid ? CARD_FIELD_MESSAGES[field] : undefined;
	};

	const addressError = (field: AddressField) => {
		const required = (touched[field] || submitted) && addressErrors ? addressErrors[field] : undefined;
		return required || tokenErrors[field];
	};

	const dirty = ELEMENT_FIELDS.some((field) => !cardState[field].empty)
		|| (Object.keys(address) as AddressField[]).some((field) => address[field] !== initialAddress[field]);

	const handleAddressChange = (field: AddressField, value: string) => {
		setAddress((prev) => ({ ...prev, [field]: value }));
		setTokenErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
	};

	const handleBlur = (field: AddressField) => () => setTouched((prev) => ({ ...prev, [field]: true }));

	const handleCancel = () => {
		setAddress(initialAddress);
		setTouched({});
		setSubmitted(false);
		setTokenErrors({});
		setResetKey((key) => key + 1);
	};

	useEffect(() => {
		onStateChange({ dirty, saving });
	}, [onStateChange, dirty, saving]);

	useEffect(() => () => onStateChange(null), [onStateChange]);

	const handleSubmit = () => {
		setSubmitted(true);
		const recurly = recurlyRef.current;
		const elements = elementsRef.current;
		const cardValid = ELEMENT_FIELDS.every((field) => cardState[field].valid);
		if (saving || addressErrors || !cardValid || !recurly || !elements) {
			return;
		}
		setSaving(true);
		setTokenErrors({});
		const billingInfo = {
			first_name: card?.firstName || user.clientFirstName || user.firstName || "",
			last_name: card?.lastName || user.clientLastName || user.lastName || "",
			address1: address.address1.trim(),
			// Recurly replaces billing info wholesale, so a second line has to be sent back or it is erased.
			...(card?.address2 ? { address2: card.address2 } : {}),
			city: address.city.trim(),
			state: address.state.trim(),
			postal_code: address.zip.trim(),
			country: card?.country || "US",
		};
		// A throw from Recurly.js would otherwise leave the bar on "Saving..." for good.
		try {
			recurly.token(elements, billingInfo, (error, token) => {
				if (error) {
					if (mountedRef.current) {
						setSaving(false);
					}
					const fieldErrors = error.code === "validation" ? tokenFieldErrors(error.fields) : {};
					if (Object.keys(fieldErrors).length) {
						if (mountedRef.current) {
							setTokenErrors(fieldErrors);
						}
					} else {
						onError(error.code === "validation" ? CARD_ERROR_MESSAGES.GENERIC : CARD_ERROR_MESSAGES.SERVICE_UNAVAILABLE);
					}
					return;
				}
				dispatch(updateBillingCard({ token: token.id }))
					.unwrap()
					.then(() => {
						if (mountedRef.current) {
							setSaving(false);
							setSubmitted(false);
							setTouched({});
							// The card on file may look the same as what was just entered, so the
							// placeholders can't be relied on to remount the fields empty.
							setResetKey((key) => key + 1);
						}
						onSaved();
					})
					.catch((rejected: BillingCardUpdateError) => {
						if (mountedRef.current) {
							setSaving(false);
						}
						onError(cardErrorMessage(rejected));
					});
			});
		} catch(error) {
			// eslint-disable-next-line no-console
			console.error("[billing] Recurly token request failed", error);
			setSaving(false);
			onError(CARD_ERROR_MESSAGES.SERVICE_UNAVAILABLE);
		}
	};

	useImperativeHandle(ref, () => ({ save: handleSubmit, cancel: handleCancel }));

	const renderCardField = (field: CardField, label: string, refs: React.RefObject<HTMLDivElement>[]) => {
		const error = cardError(field);
		return (
			<>
				<Mui.Typography component="label" className={classes.fieldLabel}>
					{label}
				</Mui.Typography>
				{refs.length === 1 ? (
					<div ref={refs[0]} className={cx(classes.recurlyField, error && classes.recurlyFieldError)} />
				) : (
					<div className={cx(classes.recurlyField, classes.recurlyPair, error && classes.recurlyPairError)}>
						<div ref={refs[0]} className={classes.recurlyPairHalf} />
						<span className={classes.recurlyPairSeparator}>/</span>
						<div ref={refs[1]} className={classes.recurlyPairHalf} />
					</div>
				)}
				{error && <Mui.Typography className={classes.fieldError}>{error}</Mui.Typography>}
			</>
		);
	};

	return (
		<div className={classes.fieldColumn}>
			{!ready && <Mui.LinearProgress className={classes.alert} />}
			<Mui.Grid container spacing={2}>
				<Mui.Grid item xs={12}>
					{renderCardField("number", "Card Number", [numberRef])}
				</Mui.Grid>
				<Mui.Grid item xs={6}>
					{renderCardField("expiry", "Expiration Date (MM/YY)", [monthRef, yearRef])}
				</Mui.Grid>
				<Mui.Grid item xs={6}>
					{renderCardField("cvv", "CVV / Security Code", [cvvRef])}
				</Mui.Grid>
				<Mui.Grid item xs={12}>
					<Mui.Divider className={classes.divider} />
				</Mui.Grid>
				<Mui.Grid item xs={12}>
					<FormField
						label="Billing Address"
						placeholder="123 Main St."
						value={address.address1}
						onChange={(event) => handleAddressChange("address1", event.target.value)}
						onBlur={handleBlur("address1")}
						error={!!addressError("address1")}
						helperText={addressError("address1")}
					/>
				</Mui.Grid>
				<Mui.Grid item xs={6}>
					<FormField
						label="City"
						placeholder="Eugene"
						value={address.city}
						onChange={(event) => handleAddressChange("city", event.target.value)}
						onBlur={handleBlur("city")}
						error={!!addressError("city")}
						helperText={addressError("city")}
					/>
				</Mui.Grid>
				<Mui.Grid item xs={6}>
					<FormSelectField
						label="State"
						value={address.state}
						onChange={(value) => handleAddressChange("state", value)}
						options={stateOptionsFor(address.state)}
						valueExtractor={(option) => option.code}
						labelExtractor={(option) => option.code}
						error={!!addressError("state")}
						helperText={addressError("state")}
					/>
				</Mui.Grid>
				<Mui.Grid item xs={12}>
					<FormField
						label="ZIP Code"
						placeholder="97401"
						value={address.zip}
						onChange={(event) => handleAddressChange("zip", event.target.value)}
						onBlur={handleBlur("zip")}
						error={!!addressError("zip")}
						helperText={addressError("zip")}
					/>
				</Mui.Grid>
			</Mui.Grid>
		</div>
	);
});
